import { useEffect, useState } from 'react';

import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Kbd } from '@/components/ui/kbd';
import { cn } from '@/lib/utils';

type Props = { show?: boolean };

/**
 * Floating bottom-center hint with the replay keyboard shortcuts. Auto-hides
 * after 4s and immediately on the `replay-nav-used` window event. The parent
 * decides whether to mount it (replay-only).
 */
export const ReplayKeyboardHint = ({ show = true }: Props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handle = () => setVisible(false);
    window.addEventListener('replay-nav-used', handle);
    return () => window.removeEventListener('replay-nav-used', handle);
  }, []);

  if (!show) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-8 left-1/2 z-[60] w-96 -translate-x-1/2 transition-opacity duration-500',
        !visible && 'pointer-events-none opacity-0'
      )}
    >
      <div className="flex items-center justify-between rounded-lg border bg-background p-3 text-xs text-foreground-muted shadow-md">
        <span className="flex items-center gap-1.5">
          <Kbd className="h-4 justify-center bg-background shadow-sm">Esc</Kbd>
          {t('common.close')}
        </span>
        <div className="flex items-center gap-1.5">
          <Kbd className="size-4 justify-center bg-background px-0 shadow-sm">
            <ArrowLeftIcon className="size-2.5" />
          </Kbd>
          <Kbd className="size-4 justify-center bg-background px-0 shadow-sm">
            <ArrowRightIcon className="size-2.5" />
          </Kbd>
          {t('command_palette.shortcut_navigate')}
        </div>
      </div>
    </div>
  );
};
